/**
 * Multi-Select Module
 * Checkbox dropdown allowing multiple selections
 * Supports two modes:
 * - Form mode (default): combines values into hidden inputs on form submit
 * - Instant mode (data-multi-select-mode="instant"): navigates on checkbox change
 */

export function initMultiSelect() {
    const widgets = document.querySelectorAll('[data-multi-select]');
    if (!widgets.length) return;

    widgets.forEach(setupWidget);

    // Attach form submit handlers to combine checkbox values
    const forms = new Set();
    widgets.forEach(w => {
        const form = w.closest('form');
        if (form && !forms.has(form)) {
            forms.add(form);
            form.addEventListener('submit', handleFormSubmit);
        }
    });

    // Close all menus when clicking outside
    document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-multi-select]')) {
            closeAllMenus();
        }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAllMenus();
    });

    window.addEventListener('resize', positionOpenMenus);
}

function getCheckedValues(menu, separator = '|') {
    const checked = menu.querySelectorAll('input:checked');
    return Array.from(checked).map(cb => cb.value).join(separator);
}

/**
 * For instant-mode widgets, build URL from all instant multi-selects and navigate.
 */
function navigateInstant() {
    const url = new URL(location);

    document.querySelectorAll('[data-multi-select-mode="instant"]').forEach(widget => {
        const menu = widget.querySelector('.multi-select-menu');
        const paramName = widget.getAttribute('data-param-name');
        const separator = paramName === 'amenities' ? '&&' : '|';
        const values = getCheckedValues(menu, separator);
        if (values) {
            url.searchParams.set(paramName, values);
        } else {
            url.searchParams.delete(paramName);
        }
    });

    window.location.href = url.toString();
}

function setupWidget(widget) {
    const trigger = widget.querySelector('.multi-select-trigger');
    const menu = widget.querySelector('.multi-select-menu');
    const label = widget.querySelector('.multi-select-label');
    const defaultText = label.textContent;
    const checkboxes = menu.querySelectorAll('input[type="checkbox"]');
    const isInstant = widget.getAttribute('data-multi-select-mode') === 'instant';

    // Set initial label count for pre-checked boxes
    const initialChecked = menu.querySelectorAll('input:checked');
    if (initialChecked.length) {
        label.textContent = `${defaultText} (${initialChecked.length})`;
    }

    // Toggle menu on trigger click
    trigger.addEventListener('click', () => {
        const isOpen = !menu.hidden;

        closeAllMenus();

        if (!isOpen) {
            menu.hidden = false;
            trigger.setAttribute('aria-expanded', 'true');
            requestAnimationFrame(() => positionMenu(widget));
        }
    });

    // Update label when checkboxes change; navigate immediately in instant mode
    checkboxes.forEach(cb => {
        cb.addEventListener('change', () => {
            const checked = menu.querySelectorAll('input:checked');
            label.textContent = checked.length
                ? `${defaultText} (${checked.length})`
                : defaultText;

            if (isInstant) {
                navigateInstant();
            }
        });
    });
}

/**
 * On form submit, combine multi-select checkbox values into single hidden inputs.
 * - 'type' checkboxes -> pipe-separated (Serviced|Coworking) for ProSearch
 * - 'amenities' checkboxes -> comma-separated (Meeting Rooms,Kitchen) for PHP
 */
function handleFormSubmit(e) {
    const form = e.target;

    // Collect all multi-select checkbox groups
    const groups = {};
    form.querySelectorAll('[data-multi-select] input[type="checkbox"]').forEach(cb => {
        if (!groups[cb.name]) groups[cb.name] = [];
        if (cb.checked) groups[cb.name].push(cb.value);
    });

    for (const [name, values] of Object.entries(groups)) {
        // Disable original checkboxes so they don't submit
        form.querySelectorAll(`[data-multi-select] input[name="${name}"]`).forEach(cb => {
            cb.disabled = true;
        });

        if (values.length) {
            // Create hidden input with combined value
            // Amenities use && (AND) so results must have ALL selected; types use | (OR)
            const separator = name === 'amenities' ? '&&' : '|';
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = name;
            hidden.value = values.join(separator);
            form.appendChild(hidden);
        }
    }
}

function closeAllMenus() {
    document.querySelectorAll('[data-multi-select]').forEach(widget => {
        const menu = widget.querySelector('.multi-select-menu');
        const trigger = widget.querySelector('.multi-select-trigger');
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
    });
}

function positionOpenMenus() {
    document.querySelectorAll('[data-multi-select]').forEach(widget => {
        const menu = widget.querySelector('.multi-select-menu');
        if (menu && !menu.hidden) {
            positionMenu(widget);
        }
    });
}

function positionMenu(widget) {
    const menu = widget.querySelector('.multi-select-menu');
    if (!menu || menu.hidden) return;

    const viewportGutter = 16;
    const visualViewport = window.visualViewport;
    const viewportLeft = Math.round(visualViewport?.offsetLeft || 0);
    const viewportWidth = Math.round(
        visualViewport?.width || document.documentElement.clientWidth || window.innerWidth || 0
    );
    const maxMenuWidth = Math.max(viewportWidth - (viewportGutter * 2), 0);

    menu.style.left = '0px';
    menu.style.right = 'auto';
    menu.style.maxWidth = `${maxMenuWidth}px`;

    if (menu.classList.contains('multi-select-menu--wide')) {
        menu.style.width = `${Math.min(420, maxMenuWidth)}px`;
    } else {
        menu.style.width = '';
    }

    const widgetRect = widget.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const minLeft = viewportLeft + viewportGutter;
    const maxLeft = Math.max(minLeft, viewportLeft + viewportWidth - viewportGutter - menuRect.width);
    const clampedLeft = Math.min(Math.max(widgetRect.left, minLeft), maxLeft);

    menu.style.left = `${clampedLeft - widgetRect.left}px`;
}
